// src/modules/orders/Orders.tsx
import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Space,
  Tag,
  Typography,
  Statistic,
  Badge,
  InputNumber,
  Tooltip,
  Popconfirm,
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  EditOutlined,
  EyeOutlined,
  FilterOutlined,
  DownloadOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import type { ColumnsType, TableRowSelection } from 'antd/es/table/interface';
import { useOrders } from '@/hooks/useOrders';
import { Order } from '@/services/api/orders';
import { OrderDetailModal } from './components/OrderDetailModal';
import { OrderStatusModal } from './components/OrderStatusModal';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;

// Статусы заказов с цветами
const ORDER_STATUS_MAP = {
  pending: { color: 'orange', text: 'Ожидает' },
  confirmed: { color: 'blue', text: 'Подтвержден' },
  preparing: { color: 'yellow', text: 'Формируется' },
  delivering: { color: 'cyan', text: 'Доставляется' },
  completed: { color: 'green', text: 'Завершен' },
  cancelled: { color: 'red', text: 'Отменен' },
  abandoned: { color: 'gray', text: 'Брошенная корзина' },
};

// Статусы оплаты с цветами
const PAYMENT_STATUS_MAP = {
  pending: { color: 'orange', text: 'Ожидает' },
  paid: { color: 'green', text: 'Оплачен' },
  failed: { color: 'red', text: 'Ошибка' },
  refunded: { color: 'purple', text: 'Возвращен' },
};

// Расширенный интерфейс для фильтров
interface FilterParams {
  status: string;
  payment_status: string;
  search: string;
  date_range: any[];
  min_amount?: number;
  max_amount?: number;
  page?: number;
  per_page?: number;
  date_from?: string;
  date_to?: string;
}

// Функция получения текущей недели
const getCurrentWeek = () => {
  const today = dayjs();
  const startOfWeek = today.startOf('week');
  const endOfWeek = today.endOf('week');
  return [startOfWeek, endOfWeek];
};

export const Orders: React.FC = () => {
  const {
    orders,
    ordersData,
    currentOrder,
    analytics,
    loading,
    ordersLoading,
    loadOrders,
    loadOrderDetail,
    updateOrderStatus,
    bulkUpdateOrders,
    updateInvoiceFlag, // ✨ НОВЫЙ МЕТОД
    loadAnalytics,
    clearOrderDetail,
    refresh,
  } = useOrders();

  // Состояние фильтров с текущей неделей по умолчанию
  const [filters, setFilters] = useState<FilterParams>({
    status: '',
    payment_status: '',
    search: '',
    date_range: getCurrentWeek(),
    min_amount: undefined,
    max_amount: undefined,
  });

  // Состояние модальных окон
  const [orderDetailModal, setOrderDetailModal] = useState(false);
  const [statusUpdateModal, setStatusUpdateModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [selectedOrderCurrentStatus, setSelectedOrderCurrentStatus] = useState<string>('');
  const [newStatus, setNewStatus] = useState('');
  const [updateComment, setUpdateComment] = useState('');

  // Массовое выделение
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [bulkAction, setBulkAction] = useState('');

  // Применение фильтров с синхронизацией аналитики
  const applyFilters = () => {
    const params: FilterParams = {
      ...filters,
      page: 1,
      per_page: 20,
    };

    let analyticsParams = {};

    if (filters.date_range && filters.date_range.length === 2) {
      params.date_from = dayjs(filters.date_range[0]).format('YYYY-MM-DD');
      params.date_to = dayjs(filters.date_range[1]).format('YYYY-MM-DD');
      
      analyticsParams = {
        date_from: params.date_from,
        date_to: params.date_to
      };
      
      const { date_range, ...paramsWithoutDateRange } = params;
      loadOrders(paramsWithoutDateRange);
    } else {
      const { date_range, ...paramsWithoutDateRange } = params;
      loadOrders(paramsWithoutDateRange);
    }

    // Загружаем аналитику с теми же параметрами
    loadAnalytics(analyticsParams);
  };

  // Сброс фильтров с возвратом к текущей неделе
  const resetFilters = () => {
    const currentWeek = getCurrentWeek();
    setFilters({
      status: '',
      payment_status: '',
      search: '',
      date_range: currentWeek,
      min_amount: undefined,
      max_amount: undefined,
    });
    
    const analyticsParams = {
      date_from: dayjs(currentWeek[0]).format('YYYY-MM-DD'),
      date_to: dayjs(currentWeek[1]).format('YYYY-MM-DD')
    };
    
    loadOrders();
    loadAnalytics(analyticsParams);
  };

  // Обработка изменения страницы
  const handleTableChange = (pagination: any) => {
    const params: FilterParams = {
      ...filters,
      page: pagination.current,
      per_page: pagination.pageSize,
    };

    if (filters.date_range && filters.date_range.length === 2) {
      params.date_from = dayjs(filters.date_range[0]).format('YYYY-MM-DD');
      params.date_to = dayjs(filters.date_range[1]).format('YYYY-MM-DD');
    }

    const { date_range, ...paramsWithoutDateRange } = params;
    loadOrders(paramsWithoutDateRange);
  };

  // Просмотр деталей заказа
  const viewOrderDetail = async (orderId: number) => {
    setSelectedOrderId(orderId);
    setOrderDetailModal(true);
    await loadOrderDetail(orderId);
  };

  // Обновление статуса одного заказа
  const handleStatusUpdate = async () => {
    if (!selectedOrderId || !newStatus) return;

    try {
      await updateOrderStatus(selectedOrderId, newStatus, updateComment);
      setStatusUpdateModal(false);
      setNewStatus('');
      setUpdateComment('');
      setSelectedOrderId(null);
    } catch (error) {
      // Ошибка уже обработана в хуке
    }
  };

  // Массовое обновление статуса
  const handleBulkAction = async () => {
    if (!selectedRowKeys.length || !bulkAction) return;

    try {
      await bulkUpdateOrders(selectedRowKeys, bulkAction);
      setSelectedRowKeys([]);
      setBulkAction('');
    } catch (error) {
      // Ошибка уже обработана в хуке
    }
  };

  // ✨ НОВЫЙ ОБРАБОТЧИК: Изменение флага отправки счета
  const handleInvoiceFlagChange = async (orderId: number, currentFlag: boolean) => {
    try {
      await updateInvoiceFlag(orderId, !currentFlag);
    } catch (error) {
      // Ошибка уже обработана в хуке
    }
  };

  // Получение количества заказов по статусу
  const getStatusCount = (status: string) => {
    if (!analytics?.status_distribution) return 0;
    const statusData = analytics.status_distribution.find(item => item.status === status);
    return statusData?.count || 0;
  };

  // Конфигурация выбора строк
  const rowSelection: TableRowSelection<Order> = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => {
      setSelectedRowKeys(newSelectedRowKeys as number[]);
    },
    getCheckboxProps: (record: Order) => ({
      disabled: record.status === 'completed' || record.status === 'cancelled',
    }),
  };

  // ✨ Колонки таблицы (с новой колонкой для флага счета)
  const columns: ColumnsType<Order> = [
    {
      title: '№ Заказа',
      dataIndex: 'order_number',
      key: 'order_number',
      width: 140,
      fixed: 'left',
      render: (text: string, record: Order) => (
        <Button 
          type="link" 
          onClick={() => viewOrderDetail(record.id)}
          className="p-0"
        >
          {text}
        </Button>
      ),
    },
    {
      title: 'Клиент',
      dataIndex: 'customer_name',
      key: 'customer_name',
      width: 150,
    },
    {
      title: 'Телефон',
      dataIndex: 'customer_phone',
      key: 'customer_phone',
      width: 130,
    },
    {
      title: 'Адрес',
      dataIndex: 'full_address',
      key: 'full_address',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Сумма',
      dataIndex: 'total_price',
      key: 'total_price',
      width: 120,
      align: 'right',
      render: (price: number) => `${price.toLocaleString()} ₸`,
    },
        // 🆕 НОВАЯ КОЛОНКА: Бонусы
    {
      title: 'Бонусы',
      dataIndex: 'bonus_amount',
      key: 'bonus_amount',
      width: 110,
      render: (bonus: number, record: Order) => {
        if (bonus > 0) {
          const percent = record.total_price > 0 
            ? ((bonus / record.total_price) * 100).toFixed(1) 
            : 0;
          return (
            <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
              -{bonus.toLocaleString('ru-RU')} ₸
              <br />
              <span style={{ fontSize: '11px', color: '#8c8c8c' }}>
                ({percent}%)
              </span>
            </span>
          );
        }
        return '—';
      },
      sorter: (a: Order, b: Order) => (a.bonus_amount || 0) - (b.bonus_amount || 0),
    },
    // 🆕 НОВАЯ КОЛОНКА: К оплате
    {
      title: '💳 К оплате',
      dataIndex: 'amount_to_pay',
      key: 'amount_to_pay',
      width: 130,
      render: (amount: number) => `${amount} ₸`,
      sorter: (a: Order, b: Order) => (a.amount_to_pay || 0) - (b.amount_to_pay || 0),
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status: keyof typeof ORDER_STATUS_MAP) => (
        <Tag color={ORDER_STATUS_MAP[status]?.color}>
          {ORDER_STATUS_MAP[status]?.text}
        </Tag>
      ),
    },
    {
      title: 'Оплата',
      dataIndex: 'payment_status',
      key: 'payment_status',
      width: 120,
      render: (status: keyof typeof PAYMENT_STATUS_MAP) => (
        <Tag color={PAYMENT_STATUS_MAP[status]?.color}>
          {PAYMENT_STATUS_MAP[status]?.text}
        </Tag>
      ),
    },
    // ✨ НОВАЯ КОЛОНКА: Счет отправлен
    {
      title: 'Счет',
      dataIndex: 'invoice_sent_flag',
      key: 'invoice_sent_flag',
      width: 100,
      align: 'center',
      render: (invoiceSentFlag: boolean, record: Order) => {
        // Показываем только для заказов с ручной оплатой (pending payment)
        const isManualPayment = record.payment_status === 'pending';
        
        if (!isManualPayment) {
          return <Text type="secondary">—</Text>;
        }

        return (
          <Tooltip title={invoiceSentFlag ? 'Счет отправлен клиенту' : 'Счет не отправлен'}>
            <Popconfirm
              title={invoiceSentFlag ? 'Снять флаг отправки счета?' : 'Пометить счет как отправленный?'}
              onConfirm={() => handleInvoiceFlagChange(record.id, invoiceSentFlag)}
              okText="Да"
              cancelText="Нет"
            >
              <Button
                type="text"
                size="small"
                icon={
                  invoiceSentFlag ? (
                    <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
                  ) : (
                    <FileTextOutlined style={{ color: '#d9d9d9', fontSize: 18 }} />
                  )
                }
              />
            </Popconfirm>
          </Tooltip>
        );
      },
    },
    {
      title: 'Дата доставки',
      dataIndex: 'delivery_date',
      key: 'delivery_date',
      width: 120,
      render: (date: string) => dayjs(date).format('DD.MM.YYYY'),
    },
    {
      title: 'Создан',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record: Order) => (
        <Space size="small">
          <Tooltip title="Просмотр">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => viewOrderDetail(record.id)}
            />
          </Tooltip>
          <Tooltip title="Изменить статус">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedOrderId(record.id);
                setSelectedOrderCurrentStatus(record.status);
                setNewStatus(record.status);
                setStatusUpdateModal(true);
              }}
              disabled={record.status === 'completed' || record.status === 'cancelled'}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Загрузка базовых данных при монтировании
  useEffect(() => {
    applyFilters();
  }, []);

  return (
    <div className="orders-page">
      {/* Заголовок */}
      <div className="mb-6">
        <Title level={2}>Заказы</Title>
        <Text type="secondary">Управление заказами и их статусами</Text>
      </div>

      {/* Аналитика */}
      <div className="mb-6">
        {analytics && (
          <>
            {/* Первый ряд - статусы */}
            <Row gutter={8} className="mb-2">
              <Col span={3}>
                <Card size="small" style={{ minHeight: '70px' }}>
                  <Statistic
                    title={<span style={{ fontSize: '11px' }}>Всего</span>}
                    value={analytics.summary.total_orders}
                    valueStyle={{ fontSize: '16px', fontWeight: 'bold' }}
                    prefix={<Badge status="processing" />}
                  />
                </Card>
              </Col>
              <Col span={3}>
                <Card size="small" style={{ minHeight: '70px' }}>
                  <Statistic
                    title={<span style={{ fontSize: '11px' }}>Ожидает</span>}
                    value={getStatusCount('pending')}
                    valueStyle={{ fontSize: '16px', fontWeight: 'bold' }}
                    prefix={<Badge status="warning" />}
                  />
                </Card>
              </Col>
              <Col span={3}>
                <Card size="small" style={{ minHeight: '70px' }}>
                  <Statistic
                    title={<span style={{ fontSize: '11px' }}>Подтвержден</span>}
                    value={getStatusCount('confirmed')}
                    valueStyle={{ fontSize: '16px', fontWeight: 'bold' }}
                    prefix={<Badge status="processing" />}
                  />
                </Card>
              </Col>
              <Col span={3}>
                <Card size="small" style={{ minHeight: '70px' }}>
                  <Statistic
                    title={<span style={{ fontSize: '11px' }}>Формируется</span>}
                    value={getStatusCount('preparing')}
                    valueStyle={{ fontSize: '16px', fontWeight: 'bold' }}
                    prefix={<Badge status="processing" />}
                  />
                </Card>
              </Col>
              <Col span={3}>
                <Card size="small" style={{ minHeight: '70px' }}>
                  <Statistic
                    title={<span style={{ fontSize: '11px' }}>Доставляется</span>}
                    value={getStatusCount('delivering')}
                    valueStyle={{ fontSize: '16px', fontWeight: 'bold' }}
                    prefix={<Badge status="processing" />}
                  />
                </Card>
              </Col>
              <Col span={3}>
                <Card size="small" style={{ minHeight: '70px' }}>
                  <Statistic
                    title={<span style={{ fontSize: '11px' }}>Завершен</span>}
                    value={getStatusCount('completed')}
                    valueStyle={{ fontSize: '16px', fontWeight: 'bold' }}
                    prefix={<Badge status="success" />}
                  />
                </Card>
              </Col>
              <Col span={3}>
                <Card size="small" style={{ minHeight: '70px' }}>
                  <Statistic
                    title={<span style={{ fontSize: '11px' }}>Отменен</span>}
                    value={getStatusCount('cancelled')}
                    valueStyle={{ fontSize: '16px', fontWeight: 'bold' }}
                    prefix={<Badge status="error" />}
                  />
                </Card>
              </Col>
              <Col span={3}>
                <Card size="small" style={{ minHeight: '70px' }}>
                  <Statistic
                    title={<span style={{ fontSize: '11px' }}>Брошенная корзина</span>}
                    value={getStatusCount('abandoned')}
                    valueStyle={{ fontSize: '16px', fontWeight: 'bold' }}
                    prefix={<Badge status="default" />}
                  />
                </Card>
              </Col>
            </Row>

            {/* Второй ряд - финансовые показатели */}
            <Row gutter={8} className="mb-6">
              <Col span={12}>
                <Card size="small" style={{ minHeight: '70px' }}>
                  <Statistic
                    title={<span style={{ fontSize: '12px' }}>Выручка (без отмененных и брошенных)</span>}
                    value={analytics.summary.total_revenue}
                    suffix="₸"
                    valueStyle={{ fontSize: '18px', fontWeight: 'bold', color: '#52c41a' }}
                    prefix={<Badge status="success" />}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small" style={{ minHeight: '70px' }}>
                  <Statistic
                    title={<span style={{ fontSize: '12px' }}>Средний чек</span>}
                    value={analytics.summary.average_order_value}
                    suffix="₸"
                    precision={0}
                    valueStyle={{ fontSize: '18px', fontWeight: 'bold', color: '#1890ff' }}
                  />
                </Card>
              </Col>
            </Row>
          </>
        )}
      </div>

      {/* Фильтры */}
      <Card className="mb-4">
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Input
              placeholder="Поиск по номеру, имени, телефону..."
              prefix={<SearchOutlined />}
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              onPressEnter={applyFilters}
            />
          </Col>
          <Col span={4}>
            <Select
              placeholder="Статус заказа"
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
              allowClear
            >
              {Object.entries(ORDER_STATUS_MAP).map(([key, config]) => (
                <Option key={key} value={key}>{config.text}</Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              placeholder="Статус оплаты"
              style={{ width: '100%' }}
              value={filters.payment_status}
              onChange={(value) => setFilters(prev => ({ ...prev, payment_status: value }))}
              allowClear
            >
              {Object.entries(PAYMENT_STATUS_MAP).map(([key, config]) => (
                <Option key={key} value={key}>{config.text}</Option>
              ))}
            </Select>
          </Col>
          <Col span={6}>
            <RangePicker
              style={{ width: '100%' }}
              value={filters.date_range as any}
              onChange={(dates) => setFilters(prev => ({ ...prev, date_range: dates as any }))}
              placeholder={['От', 'До']}
            />
          </Col>
          <Col span={4}>
            <Space.Compact style={{ width: '100%' }}>
              <InputNumber
                placeholder="Мин. сумма"
                style={{ width: '50%' }}
                value={filters.min_amount}
                onChange={(value) => setFilters(prev => ({ ...prev, min_amount: value || undefined }))}
              />
              <InputNumber
                placeholder="Макс. сумма"
                style={{ width: '50%' }}
                value={filters.max_amount}
                onChange={(value) => setFilters(prev => ({ ...prev, max_amount: value || undefined }))}
              />
            </Space.Compact>
          </Col>
        </Row>
        
        <Row className="mt-4">
          <Col span={24}>
            <Space>
              <Button 
                type="primary" 
                icon={<FilterOutlined />}
                onClick={applyFilters}
                loading={ordersLoading}
              >
                Применить фильтры
              </Button>
              <Button onClick={resetFilters}>
                Сбросить
              </Button>
              <Button 
                icon={<ReloadOutlined />}
                onClick={refresh}
                loading={ordersLoading}
              >
                Обновить
              </Button>
              <Button icon={<DownloadOutlined />}>
                Экспорт
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Массовые действия */}
      {selectedRowKeys.length > 0 && (
        <Card className="mb-4">
          <Row align="middle" gutter={16}>
            <Col>
              <Text strong>Выбрано: {selectedRowKeys.length} заказов</Text>
            </Col>
            <Col>
              <Select
                placeholder="Массовое действие"
                style={{ width: 200 }}
                value={bulkAction}
                onChange={setBulkAction}
              >
                <Option value="confirmed">Подтвердить</Option>
                <Option value="preparing">В готовку</Option>
                <Option value="delivering">В доставку</Option>
                <Option value="completed">Завершить</Option>
                <Option value="cancelled">Отменить</Option>
              </Select>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<TeamOutlined />}
                onClick={handleBulkAction}
                disabled={!bulkAction}
                loading={loading}
              >
                Применить
              </Button>
            </Col>
            <Col>
              <Button onClick={() => setSelectedRowKeys([])}>
                Отменить выбор
              </Button>
            </Col>
          </Row>
        </Card>
      )}

      {/* Таблица заказов */}
      <Card>
        <Table
          columns={columns}
          dataSource={orders}
          rowKey="id"
          loading={ordersLoading}
          rowSelection={rowSelection}
          scroll={{ x: 1500 }}
          pagination={{
            current: ordersData?.current_page || 1,
            pageSize: ordersData?.per_page || 20,
            total: ordersData?.count || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} заказов`,
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* Модальное окно деталей заказа */}
      <OrderDetailModal
        visible={orderDetailModal}
        orderDetail={currentOrder}
        onClose={() => {
          setOrderDetailModal(false);
          clearOrderDetail();
        }}
      />

      {/* Модальное окно обновления статуса */}
      <OrderStatusModal
        visible={statusUpdateModal}
        loading={loading}
        currentStatus={selectedOrderCurrentStatus}
        newStatus={newStatus}
        comment={updateComment}
        onStatusChange={setNewStatus}
        onCommentChange={setUpdateComment}
        onOk={handleStatusUpdate}
        onCancel={() => {
          setStatusUpdateModal(false);
          setNewStatus('');
          setUpdateComment('');
          setSelectedOrderCurrentStatus('');
        }}
      />
    </div>
  );
};